import { ChangeDetectorRef, Component, type OnDestroy, type OnInit } from '@angular/core'
import { type HttpErrorResponse } from '@angular/common/http'
import { MatDialog } from '@angular/material/dialog'
import { type MatCheckboxChange } from '@angular/material/checkbox'
import { type Subscription } from 'rxjs'
import {
  type UpdateUserConsentsRequest, type User, type ConsentsDTO, type UserConsentsResponse, type DealerConsentsDTO, type UserLanguage, type UserConsentChannel, UserService
} from '@inside-hub-app/customer-portal-b2c-client'
import { MatomoIntegrationService, TransifexService, CustomerPortalSharedModule } from '@inside-hub-app/customer-portal-shared'
import {
  type CustomerPortalConfig, type Link
} from '@inside-hub-app/customer-portal-config'
import { TranslationService } from '@emilfreydigital/transifex-angular'
import { EfRemoteConfigurationService } from '@inside-hub-app/ef-remote-config'
import { NGXLogger } from 'ngx-logger'
import { DataService } from '../../../../services/data.service'
import { LanguageService } from '../../../../services/language.service'
import { LocalStorageService } from '../../../../services/local-storage.service'
import { SharedService } from '../../../../services/shared.service'

import { CommonModule } from '@angular/common'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'
import { HubChildTranslateModule } from '@inside-hub-app/hub-translate'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { NgxGlideModule } from 'ngx-glide'
import { GoogleMapsModule } from '@angular/google-maps'
import { A11yModule } from '@angular/cdk/a11y'
import { TextFieldModule } from '@angular/cdk/text-field'
type PrivacySubscriptions = {
  onUserLoaded: Subscription | null
  onLanguageChange: Subscription | null
  onLanguageChangeSub: Subscription | null
  onUserConsentsLoaded: Subscription | null
  onTransifexLanguageChange: Subscription | null
}

@Component({
  selector: 'customer-portal-app-revolution-privacy',
  standalone: true,
  templateUrl: './privacy.component.html',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    CustomerPortalSharedModule,
    HubChildTranslateModule,
    NgxSkeletonLoaderModule,
    NgxGlideModule,
    GoogleMapsModule,
    A11yModule,
    TextFieldModule
  ]
})
export class PrivacyComponent implements OnInit, OnDestroy {
  user: User
  lang: string | undefined
  public hasNewGDPR
  public country
  private href = ''

  privacyLinkEnv: Link

  // the consents are now returned as array but it will only ever contain one element
  consentsResponse: UserConsentsResponse
  brands: ConsentsDTO[] = []
  dealers: DealerConsentsDTO[] = []
  group: ConsentsDTO
  subGroups: ConsentsDTO[] = []
  updatedBrands: ConsentsDTO[] = []
  updatedDealers: DealerConsentsDTO[] = []
  updatedGroup: ConsentsDTO
  updatedSubGroups: ConsentsDTO[] = []
  disabled = false
  updates: ConsentsDTO[] | DealerConsentsDTO[] = []
  update = false
  public email = ''
  public editDisabled = false
  headingChannels: UserConsentChannel[] = []
  newGdprNotice = false
  primaryLang = ''

  public privacyLink: {
    url: string
    translation: string
  }

  public link = ''

  sub: PrivacySubscriptions = {
    onUserLoaded: null,
    onLanguageChange: null,
    onLanguageChangeSub: null,
    onUserConsentsLoaded: null,
    onTransifexLanguageChange: null
  }

  constructor (
    private readonly userService: UserService,
    public dialog: MatDialog,
    private readonly logger: NGXLogger,
    private readonly transifexTranslationsService: TranslationService,
    public transifexService: TransifexService,
    private readonly sharedService: SharedService,
    private readonly data: DataService,
    private readonly languageService: LanguageService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly localStorageService: LocalStorageService,
    private readonly matomoIntegrationService: MatomoIntegrationService,
    private readonly remoteConfigService: EfRemoteConfigurationService<CustomerPortalConfig>
  ) {
    this.hasNewGDPR = this.remoteConfigService.get('hasNewGDPR')
    this.country = this.remoteConfigService.get('country.code')

    this.privacyLink = {
      url: '',
      translation: ''
    }
    this.sub.onLanguageChangeSub = data.onLanguageChange.subscribe(language => {
      if (this.languageService.checkIfExists(language)) {
        this.primaryLang = language.toLowerCase()
        this.sub.onTransifexLanguageChange =
          this.transifexService.onTransifexLanguageChange.subscribe(() => {
            this.getPrivacyTranslation()
          })
      }
    })
  }

  public getStyles (): {
    'grid-template-columns': string
  } {
    const length: number =
      this.headingChannels?.length != null ? this.headingChannels.length : 0
    return {
      'grid-template-columns': `repeat(${length + 2}, 1fr)`
    }
  }

  getConsents (): void {
    this.data.getConsents()
  }

  getPrivacyTranslation (): void {
    const translation = this.transifexTranslationsService.translate(
      'Privacy notices.',
      {
        _key: 'customerPortal.customer-portal.communication.privacy.link',
        _tags: 'customer-portal, 3.1'
      }
    )
    this.setPrivacyLink(translation)
  }

  ngOnInit (): void {
    this.sub.onTransifexLanguageChange =
      this.transifexService.onTransifexLanguageChange.subscribe(() => {
        this.getPrivacyTranslation()
      })
    this.email =
      '<a href="mailto:' +
      this.remoteConfigService.get('links.privacyEmail') +
      '">' +
      this.remoteConfigService.get('links.privacyEmail') +
      '</a>'
    this.disabled = false
    this.update = false
    this.editDisabled = false
    this.newGdprNotice = this.country === 'de'
    this.editDisabled = false
    if (this.country === 'de' || this.hasNewGDPR === true) {
      this.getConsents()
    }

    this.sub.onUserLoaded = this.data.onUserLoaded.subscribe(user => {
      this.lang = undefined
      this.cdRef.detectChanges()
      this.user = user

      this.lang =
        this.user != null && user.language != null && user.language.length !== 0
          ? user.language.find((s: UserLanguage) => s.primary).language
          : ''
    })
    this.sub.onLanguageChange = this.data.onLanguageChange.subscribe(
      newLanguage => {
        this.lang = undefined
        this.cdRef.detectChanges()
        this.lang = newLanguage.toUpperCase()
      }
    )

    this.sub.onUserConsentsLoaded = this.data.onUserConsentsLoaded.subscribe(
      consents => {
        this.consentsResponse = consents
        this.setupConsents(consents)
        this.dealers = this.consentsResponse.dealers
        this.group = this.consentsResponse.group
        this.subGroups = this.consentsResponse.subGroups
        this.brands = this.consentsResponse.brands
        this.updatedDealers = this.dealers
        this.updatedGroup = this.group
        this.updatedSubGroups = this.subGroups
        this.updatedBrands = this.brands
      }
    )
  }

  setupConsents (consents: UserConsentsResponse): void {
    let consentSize = 0
    const sources = [
      consents.group ? [consents.group] : [],
      consents.brands ?? [],
      consents.dealers ?? []
    ]

    for (const source of sources.flat()) {
      const channels = source.consents?.[0]?.channels ?? []
      if (channels.length > consentSize) {
        consentSize = channels.length
        this.headingChannels = channels
      }
    }
  }

  setPrivacyLink (privacy: string): void {
    this.privacyLink.translation = privacy
    this.setHrefLinks()
  }

  updateConsents (): void {
    this.matomoIntegrationService.trackEvent(
      'My account',
      'Data protection change'
    )

    this.disabled = true
    this.href = window.location.href
    const newConsents: UserConsentsResponse = {
      group: this.updatedGroup,
      dealers: this.updatedDealers,
      brands: this.updatedBrands,
      subGroups: this.updatedSubGroups
    }

    const updateConsents: UpdateUserConsentsRequest = {
      url: this.href,
      userConsents: newConsents
    }

    this.userService.putConsents(updateConsents).subscribe(
      (text: string) => {
        this.logger.debug(text)
        this.disabled = false
        this.sharedService.openConfirmationPopup(
          'shared.success',
          'customerPortal.customer-portal.details.change.consents-changed'
        )
        if (this.country === 'de' || this.hasNewGDPR === true) {
          this.getConsents()
        }
      },
      (error: HttpErrorResponse) => {
        this.sharedService.openConfirmationPopup(
          'shared.error',
          'shared.general-error-message'
        )
        this.logger.error(error)
      }
    )
  }

  getConsentGroup (type: string): boolean {
    const consent = this.group.consents[0]
    const val = consent.channels.find((s: UserConsentChannel) => s.name === type)
    if (val == null) {
      return false
    }
    return val.allow
  }

  getConsentSubGroup (id: number, type: string): boolean {
    const val = this.subGroups
      .find((s: ConsentsDTO) => s.id === id)
      ?.consents[0].channels.find((k: UserConsentChannel) => k.name === type)
    if (val == null) {
      return false
    }
    return val.allow
  }

  getConsentBrand (id: number, type: string): boolean {
    const val = this.brands
      .find((s: ConsentsDTO) => s.id === id)
      ?.consents[0].channels.find((k: UserConsentChannel) => k.name === type)
    if (val == null) {
      return false
    }
    return val.allow
  }

  getConsentDealer (id: number, type: string): boolean {
    const val = this.dealers
      .find((s: DealerConsentsDTO) => s.id === id)
      ?.consents[0].channels.find((k: UserConsentChannel) => k.name === type)
    if (val == null) {
      return false
    }
    return val.allow
  }

  findUserPhone (): boolean {
    return !(
      this.sharedService.findUserPhone(this.user)?.mobile != null ||
      this.sharedService.findUserPhone(this.user)?.mobileBusiness != null ||
      this.sharedService.findUserPhone(this.user)?.landlinePersonal != null ||
      this.sharedService.findUserPhone(this.user)?.landlineBusiness != null
    )
  }

  updateConsentValue (consent: string, id: number, type: string, event: MatCheckboxChange): void {
    if (consent === 'dealer') {
      this.isValidConsents(id, this.updatedDealers, event, type)
    } else if (consent === 'brand') {
      this.isValidConsents(id, this.updatedBrands, event, type)
    } else if (consent === 'subGroup') {
      this.isValidConsents(id, this.updatedSubGroups, event, type)
    } else {
      this.isValidConsents(id, this.updatedGroup, event, type)
    }
  }

  errorMessage (type: string): void {
    if (type === 'telephone') {
      if (this.findUserPhone()) {
        this.sharedService.openConfirmationPopup(
          'shared.error',
          'customerPortal.customer-portal.no-phone'
        )
      }
    }

    if (type === 'post') {
      if (!this.addressExist()) {
        this.sharedService.openConfirmationPopup(
          'shared.error',
          'customerPortal.customer-portal.no-address'
        )
      }
    }
  }

  addressExist (): boolean {
    return this.user?.address != null
  }

  isValidConsents (id: number, element: ConsentsDTO | ConsentsDTO[] | DealerConsentsDTO[], event: MatCheckboxChange, type: string): void {
    const consentValue = Array.isArray(element)
      ? element
        ?.find(d => d.id === id)
        ?.consents[0].channels.find((cd: UserConsentChannel) => cd.name === type)
      : element?.consents[0].channels.find((cd: UserConsentChannel) => cd.name === type)
    consentValue.allow = event.checked

    if (!this.updates.includes(consentValue)) {
      this.updates.push(consentValue)
    } else {
      const index = this.updates.indexOf(consentValue)
      if (index > -1) {
        this.updates.splice(index, 1)
      }
    }
  }

  checkIfDisabled (dealer: DealerConsentsDTO, channel: string): boolean {
    return !dealer.consents[0].channels.some((s: UserConsentChannel) => s?.name === channel)
  }

  checkIfExist (dealer: ConsentsDTO | DealerConsentsDTO, channel: string): boolean {
    if (
      this.editDisabled ||
      !dealer.consents[0].channels.some((s: UserConsentChannel) => s?.name === channel) ||
      (this.findUserPhone() && channel === 'telephone') ||
      (!this.addressExist() && channel === 'post')
    ) {
      return false
    }
    return true
  }

  setHrefLinks (): void {
    if (this.primaryLang == null) {
      this.primaryLang = this.getLang()
    }

    const privacyLink: Link = {
      link: this.remoteConfigService.get('links.privacyLink.link'),
      link_de: this.remoteConfigService.get('links.privacyLink.link_de'),
      link_it: this.remoteConfigService.get('links.privacyLink.link_it'),
      link_fr: this.remoteConfigService.get('links.privacyLink.link_fr'),
      eventLabel: this.remoteConfigService.get('links.privacyLink.eventLabel')
    }

    this.privacyLinkEnv = Object.assign({}, privacyLink)
    this.privacyLinkEnv.link = this.languageService.getLink(
      this.privacyLinkEnv,
      this.primaryLang
    )

    this.privacyLink.url = this.privacyLinkEnv.link
    this.link =
      '<a target="_blank" href="' +
      this.privacyLink.url +
      '">' +
      this.privacyLink.translation +
      '</a>'
  }

  getLang (): string {
    try {
      const lang = this.localStorageService.getLang()
      return this.sharedService.stringExists(lang) ? lang : 'de'
    } catch (error) {
      return 'de'
    }
  }

  ngOnDestroy (): void {
    this.sharedService.unsubscribe(this.sub)
  }

  getTranslation1 (): string {
    return this.transifexTranslationsService.translate('Privacy notice', {
      _key: 'customerPortal.customer-portal.privacy.privacynotice',
      _tags: 'customer-portal, 3.1'
    })
  }

  getTranslation2 (): string {
    return this.transifexTranslationsService.translate(
      'Gdpr personal data categories',
      {
        _key: 'customerPortal.customer-portal.communication.gdpr.personal-data-categories',
        _tags: 'customer-portal, 3.1'
      }
    )
  }

  getTranslation3 (): string {
    return this.transifexTranslationsService.translate('Gdpr purposes', {
      _key: 'customerPortal.customer-portal.communication.gdpr.purposes',
      _tags: 'customer-portal, 3.1'
    })
  }

  getTranslation4 (): string {
    return this.transifexTranslationsService.translate(
      'Further information on data protection and your rights',
      {
        _key: 'customerPortal.customer-portal.communication.gdpr.bottom-notice3',
        _tags: 'customer-portal, 3.1',
        link: this.link
      }
    )
  }

  getTranslation5 (): string {
    return this.transifexTranslationsService.translate(
      'The declaration of consent can be revoked or changed at any time for the future and without giving reasons.',
      {
        _key: 'customerPortal.customer-portal.communication.gdpr-notice-email',
        _tags: 'customer-portal, 3.1',
        email: this.email
      }
    )
  }
}
